import {
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { Info, Loader2 } from "lucide-react"
import {
  MAX_CUSTOM_INSTRUCTION_LENGTH,
  MIN_STORY_PARAGRAPHS,
  MAX_STORY_PARAGRAPHS,
  defaultStoryParagraphs,
} from "@yomimono/types"

import { ApiError } from "@/lib/api"
import { generateStory, getStories } from "@/lib/api/stories"
import { getUserProgress } from "@/lib/api/user"
import { cn } from "@/lib/utils"
import { useIsAdmin } from "@/hooks/use-is-admin"
import { useIsDesktop } from "@/hooks/use-is-desktop"
import { ResponsiveDialog } from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Field } from "@/components/ui/field"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const FORM_ID = "create-story-form"
const DAILY_LIMIT = 3

// Uppercase caption above a grouped card in the phone layout.
const groupLabelClass = "pl-4 text-[13px] text-muted-foreground uppercase"
const groupCardClass = "flex flex-col rounded-xl bg-[var(--surface-card)] px-4"

export function CreateStoryDialog({
  trigger,
  triggerLabel = "Create new story",
}: {
  /** Element that opens the dialog. Defaults to a button showing `triggerLabel`. */
  trigger?: ReactElement
  triggerLabel?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [customInstruction, setCustomInstruction] = useState("")
  const [showCustom, setShowCustom] = useState(false)
  // null until the reader moves the slider; falls back to the chapter-derived
  // default so the preselected value tracks progress as it loads.
  const [lengthOverride, setLengthOverride] = useState<number | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()
  const isDesktop = useIsDesktop()

  const { data: progress } = useQuery({
    queryKey: ["user-progress"],
    queryFn: getUserProgress,
  })

  const storyLength =
    lengthOverride ?? defaultStoryParagraphs(progress?.chapterNumber ?? 1)

  const { data: recentStories, isSuccess: storiesLoaded } = useQuery({
    queryKey: ["stories", { limit: DAILY_LIMIT }],
    queryFn: () => getStories({ limit: DAILY_LIMIT }),
  })

  const storiesInLastDay = (recentStories ?? []).filter(
    (s) => Date.now() - new Date(s.createdAt).getTime() < 24 * 60 * 60 * 1000
  )
  const canGenerate =
    isAdmin || (storiesLoaded && storiesInLastDay.length < DAILY_LIMIT)
  const oldestInWindow = storiesInLastDay[storiesInLastDay.length - 1]
  const nextGenerateAt =
    !canGenerate && oldestInWindow
      ? new Date(
          new Date(oldestInWindow.createdAt).getTime() + 24 * 60 * 60 * 1000
        )
      : null

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: () =>
      generateStory({
        length: storyLength,
        ...(customInstruction.trim()
          ? { customInstruction: customInstruction.trim() }
          : {}),
      }),
    onSuccess: (story) => {
      queryClient.invalidateQueries({ queryKey: ["stories"] })
      navigate({ to: "/stories/$id", params: { id: story.id } })
    },
  })

  const formatTime = (date: Date) => {
    const diff = date.getTime() - Date.now()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return
    if (!next) {
      setShowCustom(false)
      setCustomInstruction("")
      setLengthOverride(null)
    }
    setOpen(next)
  }

  const tooLong = customInstruction.length > MAX_CUSTOM_INSTRUCTION_LENGTH
  const submitDisabled = isPending || !canGenerate || tooLong

  const lessonSentence = (
    <>
      We'll create a story from the grammar and vocabulary you've learned.
      {progress && (
        <>
          {" "}
          You're at lesson{" "}
          <span className="font-semibold text-foreground">
            {progress.chapterNumber}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-foreground">
            {progress.textbookTitle}
          </span>
          .
        </>
      )}
    </>
  )

  const lengthSlider = (
    <Slider
      min={MIN_STORY_PARAGRAPHS}
      max={MAX_STORY_PARAGRAPHS}
      value={storyLength}
      onValueChange={(value) =>
        setLengthOverride(Array.isArray(value) ? value[0] : value)
      }
      disabled={isPending}
      aria-label="Story length in paragraphs"
    />
  )

  const shorterLonger = (className: string) => (
    <div
      className={cn("flex justify-between text-muted-foreground", className)}
    >
      <span>Shorter</span>
      <span>Longer</span>
    </div>
  )

  const counter = (
    <div
      className={cn(
        "text-right text-xs",
        tooLong ? "text-destructive" : "text-muted-foreground"
      )}
    >
      {customInstruction.length}/{MAX_CUSTOM_INSTRUCTION_LENGTH}
    </div>
  )

  const placeholder =
    "E.g. I want to practice comparing things and counting.\nAlso… some yummy food please :)"

  const errorMessage =
    isError &&
    (error instanceof ApiError && error.status === 503 ? (
      <p className="text-center text-sm text-destructive">
        Story generation failed. Please try again later.
      </p>
    ) : (
      <p className="text-center text-sm text-destructive">
        Something went wrong generating the story. Please try again.
      </p>
    ))

  const submitContent = (
    <>
      {isPending && (
        <Loader2 data-icon="inline-start" className="size-5 animate-spin" />
      )}
      {isPending ? "Creating…" : "Create story"}
    </>
  )

  const onSubmit: ComponentProps<"form">["onSubmit"] = (event) => {
    event.preventDefault()
    mutate()
  }

  if (isDesktop) {
    return (
      <ResponsiveDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="What should we read today?"
        trigger={trigger ?? <Button>{triggerLabel}</Button>}
        className="sm:max-w-lg [&_[data-slot=dialog-title]]:text-xl"
        showCloseButton={!isPending}
      >
        <form id={FORM_ID} className="flex flex-col gap-6" onSubmit={onSubmit}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">{lessonSentence}</p>

            {showCustom && (
              <Field>
                <Textarea
                  id="story-custom-prompt"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={customInstruction}
                  onChange={(event) => setCustomInstruction(event.target.value)}
                  rows={4}
                  className="min-h-28 rounded-2xl px-4"
                  placeholder={placeholder}
                  disabled={isPending}
                />
                {counter}
              </Field>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium">Story length</span>
              <span className="text-muted-foreground">
                {storyLength} paragraphs
              </span>
            </div>
            {lengthSlider}
            {shorterLonger("text-xs")}
          </div>

          {errorMessage}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="link"
              onClick={() => setShowCustom((prev) => !prev)}
              disabled={isPending}
              className="px-0 text-muted-foreground"
            >
              {showCustom
                ? "Hide custom instructions"
                : "Add custom instructions"}
            </Button>
            <Tooltip>
              <TooltipTrigger className="ml-auto flex items-center">
                <Info className="size-4 cursor-default text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top">
                You can generate up to {DAILY_LIMIT} stories every 24 hours.
                {nextGenerateAt && (
                  <> Next story available in {formatTime(nextGenerateAt)}.</>
                )}
              </TooltipContent>
            </Tooltip>
            <Button type="submit" size="lg" disabled={submitDisabled}>
              {submitContent}
            </Button>
          </div>
        </form>
      </ResponsiveDialog>
    )
  }

  // Phone layout: bottom sheet per the iOS "Create" mock. Custom instructions
  // are always visible and the submit button is pinned in the sheet footer.
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="New story"
      trigger={trigger ?? <Button>{triggerLabel}</Button>}
      showCloseButton={!isPending}
      className="bg-background [&_[data-slot=sheet-footer]]:px-4 [&_[data-slot=sheet-footer]]:pt-3 [&_[data-slot=sheet-header]]:items-center [&_[data-slot=sheet-title]]:text-[17px] [&_[data-slot=sheet-title]]:font-semibold"
      footer={
        <Button
          type="submit"
          form={FORM_ID}
          disabled={submitDisabled}
          className="h-13 w-full rounded-[14px] text-[17px] font-semibold"
        >
          {submitContent}
        </Button>
      }
    >
      <form
        id={FORM_ID}
        className="-mx-2 flex flex-col gap-5.5 pt-1"
        onSubmit={onSubmit}
      >
        <div className="flex flex-col gap-2 px-1">
          <h3 className="text-[28px] leading-tight font-bold text-[var(--heading)]">
            What should we read today?
          </h3>
          <p className="text-[15px] leading-normal text-muted-foreground">
            {lessonSentence}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className={groupLabelClass}>Story length</h4>
          <div className={cn(groupCardClass, "gap-2.5 py-3.5")}>
            <div className="flex items-baseline text-[17px]">
              <span className="flex-1">Length</span>
              <span className="text-muted-foreground">
                {storyLength} paragraphs
              </span>
            </div>
            {lengthSlider}
            {shorterLonger("text-[13px]")}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className={groupLabelClass}>
            <label htmlFor="story-custom-prompt">Custom instructions</label>
          </h4>
          <div className={cn(groupCardClass, "gap-1.5 py-3")}>
            <Textarea
              id="story-custom-prompt"
              value={customInstruction}
              onChange={(event) => setCustomInstruction(event.target.value)}
              rows={4}
              className="min-h-24 resize-none rounded-none border-0 bg-transparent! p-0 text-[17px] shadow-none focus-visible:ring-0"
              placeholder={placeholder}
              disabled={isPending}
            />
            {counter}
          </div>
          <p className="px-4 text-[13px] leading-snug text-muted-foreground">
            Optional. You can create up to {DAILY_LIMIT} stories every 24 hours.
            {nextGenerateAt && (
              <> Next story available in {formatTime(nextGenerateAt)}.</>
            )}
          </p>
        </div>

        {errorMessage}
      </form>
    </ResponsiveDialog>
  )
}
